import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../../campaignSetupAgent.enums';
import getStrategies, {FeedType} from '../../../../utils/getStrategies';
import {clinchEnums} from '@cl/shared';
import {CLEnumKeys, CLEnumType} from 'common/src/modules/enums/enums.types';
import {
  ProductSelectionStrategyType,
  ProductStrategyMetadata_UI,
} from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.types';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import {strategyIdPrefixes} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.consts';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import productSelectionStrategiesApi from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.api';

const inputSchema = z.object({
  feedType: z.nativeEnum(FeedType).describe('The feed type the user is working with'),
  strategyIds: z.array(z.string()).min(1).describe(describeIdFormat(strategyIdPrefixes)),
});

const STRATEGY_ID_NOTE =
  'Note: Strategy IDs are internal tool parameters and should not be exposed to end users.';

const REQUIREMENTS_VS_SETUP_NOTE =
  'Note: During campaign setup, card configuration can be partial and no requirement is strictly mandatory at that stage. However, all listed requirements must be satisfied for the campaign version to be valid, servable, and activatable.';

type BaseStrategy = {
  id: number;
  name: string;
  description: string;
};

type LocationStrategyDetails = {
  usesUserLocation?: boolean;
  requiresUserProfile?: boolean;
  requiresMaxDistance?: boolean;
  optionalLocationSetSelection?: boolean;
  requiresDspProvidedZip?: boolean;
  requiresPlacementLocationIdParameter?: boolean;
  requiresTargetingZipsMapping?: boolean;
  requiresTargetingDmasMapping?: boolean;
  requiresSpecificLocationIds?: boolean;
  usesUserZipAndCountry?: boolean;
  usesUserDma?: boolean;
};

type ContentStrategyDetails = {
  requiresSpecificContentItemId?: boolean;
  requiresMatchingConditions?: boolean;
  optionalContentSetSelection?: boolean;
};

const LOCATION_STRATEGY_DETAILS_BY_ID: Record<number, LocationStrategyDetails> = {
  0: {
    usesUserLocation: true,
    optionalLocationSetSelection: true,
    requiresMaxDistance: true,
  },
  2: {
    usesUserLocation: true,
    requiresUserProfile: true,
  },
  10: {
    usesUserZipAndCountry: true,
    requiresTargetingZipsMapping: true,
  },
  12: {
    requiresDspProvidedZip: true,
    requiresTargetingZipsMapping: true,
  },
  20: {
    usesUserDma: true,
    requiresTargetingDmasMapping: true,
  },
  21: {
    requiresPlacementLocationIdParameter: true,
  },
  30: {
    requiresSpecificLocationIds: true,
  },
};

const CONTENT_STRATEGY_DETAILS_BY_ID: Record<number, ContentStrategyDetails> = {
  0: {
    requiresSpecificContentItemId: true,
  },
  1: {
    requiresMatchingConditions: true,
    optionalContentSetSelection: true,
  },
  2: {
    optionalContentSetSelection: true,
  },
};

function formatMissingStrategies(missingStrategies: string[]): string {
  if (missingStrategies.length === 1) {
    return `Strategies ${missingStrategies[0]} were not found.`;
  }

  if (missingStrategies.length === 2) {
    return `Strategies ${missingStrategies[0]} and ${missingStrategies[1]} were not found.`;
  }

  const allButLast = missingStrategies.slice(0, -1).join(', ');
  const last = missingStrategies[missingStrategies.length - 1];

  return `Strategies ${allButLast} and ${last} were not found.`;
}

function formatOutputsLine(hasPartialSuccess: boolean): string {
  if (hasPartialSuccess) {
    return 'Card has three outputs: "Success", "Partial Success" and "Failure".';
  }

  return 'Card has two outputs: "Success" and "Failure".';
}

function getGlobalFeedRequirementLine(feedType: FeedType): string {
  if (feedType === FeedType.Product) {
    return 'All strategies below require selection of a Product feed.';
  }

  if (feedType === FeedType.Location) {
    return 'All strategies below require selection of a Location feed.';
  }

  return 'All strategies below require selection of a Content feed.';
}

function getCampaignTypeUiNames(campaignTypeIds: number[]): string[] {
  const campaignTypes =
    clinchEnums.loadedEnumsSubject.data[CLEnumType.CampaignPage]?.[
      CLEnumKeys.CampaignPage.CampaignType
    ]?.enumsForSelector ?? [];

  const campaignTypeById = new Map(
    campaignTypes.map((campaignType) => [+campaignType.value, campaignType.uiName]),
  );

  return campaignTypeIds.map(
    (campaignTypeId) => campaignTypeById.get(campaignTypeId) ?? String(campaignTypeId),
  );
}

function formatProductStrategy(strategy: ProductStrategyMetadata_UI): string {
  const requirements: string[] = [];
  const {flags} = strategy;

  if (flags.topItemsSelectionIsMandatory) {
    requirements.push('Requires top items list selection.');
  } else if (flags.topItemsSelectionEnabled) {
    requirements.push('Optional top items list selection.');
  }

  if (flags.categoryTopItemSelectionIsMandatory) {
    requirements.push('Requires category top items list selection.');
  } else if (flags.categoryTopItemsSelectionEnabled) {
    requirements.push('Optional category top items list selection.');
  }

  if (flags.productSetSelectionIsMandatory) {
    requirements.push('Requires product set selection.');
  } else if (flags.productSetSelectionEnabled) {
    requirements.push('Optional product set selection.');
  }

  if (flags.requiresStore) {
    requirements.push('Requires location/store from previous cards.');
  }

  if (flags.requiresUserLocation) {
    requirements.push('Uses user location.');
  }

  if (flags.requiresProfile) {
    requirements.push('Requires identifying and retrieving the user profile.');
  }

  if (flags.usesContextualDspParameter) {
    requirements.push('Requires the DSP to provide the "Contextual" parameter.');
  }

  if (flags.usesProductIdsDspParameter) {
    requirements.push(
      'Requires the DSP to provide the "Product IDs" parameter with valid product IDs that exist in the feed.',
    );
  }

  if (flags.productSetMaxItems) {
    requirements.push(`Product set maximum items: ${flags.productSetMaxItems}.`);
  }

  if (strategy.strategy === ProductSelectionStrategyType.SpecificProducts) {
    requirements.push('Requires definition of specific product IDs.');
  }

  const lines = [
    `ID: ${idHelper.toProductStrategyId(strategy.strategy)}`,
    `Name: ${strategy.uiName}`,
  ];

  if (strategy.uiTitle) {
    lines.push(`Title: ${strategy.uiTitle}`);
  }

  lines.push(`Purpose: ${strategy.purposeForAI}`);

  if (strategy.uiDescription) {
    lines.push(`Description: ${strategy.uiDescription}`);
  }

  if (strategy.supportedProductTypes?.length) {
    lines.push(`Supported product types: ${strategy.supportedProductTypes.join(', ')}.`);
  }

  if (strategy.supportedCampaignTypes?.length) {
    const supportedCampaignTypeNames = getCampaignTypeUiNames(strategy.supportedCampaignTypes);
    lines.push(`Supported campaign types: ${supportedCampaignTypeNames.join(', ')}.`);
  }

  lines.push(...requirements);
  lines.push(formatOutputsLine(flags.hasPartialSuccess));

  return lines.join('\n');
}

function formatLocationRequirements(details: LocationStrategyDetails): string[] {
  const requirements: string[] = [];

  if (details.usesUserLocation) {
    requirements.push('Uses user location.');
  }

  if (details.optionalLocationSetSelection) {
    requirements.push('Optional location set selection.');
  }

  if (details.requiresMaxDistance) {
    requirements.push('Requires max distance definition.');
  }

  if (details.requiresUserProfile) {
    requirements.push('Requires identifying and retrieving the user profile.');
  }

  if (details.usesUserZipAndCountry) {
    requirements.push('Uses user zip and country.');
  }

  if (details.requiresTargetingZipsMapping) {
    requirements.push('Feed must include Targeting Zips mapping.');
  }

  if (details.requiresDspProvidedZip) {
    requirements.push('Requires DSP-provided zip parameter.');
  }

  if (details.usesUserDma) {
    requirements.push('Uses user DMA.');
  }

  if (details.requiresTargetingDmasMapping) {
    requirements.push('Feed must include Targeting DMAs mapping.');
  }

  if (details.requiresPlacementLocationIdParameter) {
    requirements.push('Requires placement "Show location Id" parameter.');
  }

  if (details.requiresSpecificLocationIds) {
    requirements.push('Requires definition of specific location IDs.');
  }

  return requirements;
}

function formatContentRequirements(details: ContentStrategyDetails): string[] {
  const requirements: string[] = [];

  if (details.requiresSpecificContentItemId) {
    requirements.push('Requires definition of a specific content item ID.');
  }

  if (details.requiresMatchingConditions) {
    requirements.push('Requires definition of matching conditions.');
  }

  if (details.optionalContentSetSelection) {
    requirements.push('Optional content set selection.');
  }

  return requirements;
}

function formatLocationStrategy(strategy: BaseStrategy): string {
  const details = LOCATION_STRATEGY_DETAILS_BY_ID[strategy.id] ?? {};
  const lines = [
    `ID: ${idHelper.toLocationStrategyId(strategy.id)}`,
    `Name: ${strategy.name}`,
    `Description: ${strategy.description}`,
    ...formatLocationRequirements(details),
    'Card has two outputs: "Success" and "Failure".',
  ];

  return lines.join('\n');
}

function formatContentStrategy(strategy: BaseStrategy): string {
  const details = CONTENT_STRATEGY_DETAILS_BY_ID[strategy.id] ?? {};
  const lines = [
    `ID: ${idHelper.toContentStrategyId(strategy.id)}`,
    `Name: ${strategy.name}`,
    `Description: ${strategy.description}`,
    ...formatContentRequirements(details),
    'Card has two outputs: "Success" and "Failure".',
  ];

  return lines.join('\n');
}

function getSelectionStrategyDetailsTool(store: CLStore) {
  async function execute({
    input: {feedType, strategyIds},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    try {
      const campaign = store.getState().campaignPage.campaignTree.campaign;

      if (!campaign) {
        return toolError('Campaign not found');
      }

      const parsedIds = strategyIds.map((rawId) => {
        const result = idHelper.parseStrategyId(rawId);

        return {
          rawId,
          parsedId: result.ok ? result.numericId : NaN,
          isValid: result.ok,
        };
      });

      const notFoundIds: string[] = parsedIds.filter((p) => !p.isValid).map((p) => p.rawId);
      const outputBlocks: string[] = [];
      const validIds = parsedIds.filter((p) => p.isValid);

      if (feedType === FeedType.Product) {
        const strategyById = new Map(
          (await productSelectionStrategiesApi.getProductStrategies(campaign.id)).map(
            (strategy) => [strategy.strategy, strategy],
          ),
        );

        for (const strategyId of validIds) {
          const strategy = strategyById.get(strategyId.parsedId);

          if (!strategy) {
            notFoundIds.push(strategyId.rawId);
            continue;
          }

          outputBlocks.push(formatProductStrategy(strategy));
        }
      }

      if (feedType === FeedType.Location) {
        const strategyById = new Map(
          getStrategies(FeedType.Location, campaign.type).map((strategy) => [
            strategy.strategyId,
            {id: strategy.strategyId, name: strategy.name, description: strategy.description},
          ]),
        );

        for (const strategyId of validIds) {
          const strategy = strategyById.get(strategyId.parsedId);

          if (!strategy) {
            notFoundIds.push(strategyId.rawId);
            continue;
          }

          outputBlocks.push(formatLocationStrategy(strategy));
        }
      }

      if (feedType === FeedType.Content) {
        const strategyById = new Map(
          getStrategies(FeedType.Content, campaign.type).map((strategy) => [
            strategy.strategyId,
            {id: strategy.strategyId, name: strategy.name, description: strategy.description},
          ]),
        );

        for (const strategyId of validIds) {
          const strategy = strategyById.get(strategyId.parsedId);

          if (!strategy) {
            notFoundIds.push(strategyId.rawId);
            continue;
          }

          outputBlocks.push(formatContentStrategy(strategy));
        }
      }

      const output = outputBlocks.join('\n\n');
      const globalFeedRequirementLine = getGlobalFeedRequirementLine(feedType);
      const outputWithGlobalLine = output
        ? `${globalFeedRequirementLine}\n\n${output}`
        : globalFeedRequirementLine;

      const footerNotes = [REQUIREMENTS_VS_SETUP_NOTE, STRATEGY_ID_NOTE].join('\n\n');

      if (!notFoundIds.length) {
        return toolResult(`${outputWithGlobalLine}\n\n${footerNotes}`);
      }

      const missingText = formatMissingStrategies(notFoundIds);
      const finalOutput = `${outputWithGlobalLine}\n\n${missingText}`;

      return toolResult(`${finalOutput}\n\n${footerNotes}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      return toolError(`Error getting full strategy details: ${message}`);
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.GetSelectionStrategyDetails,
    displayName: 'Get strategy details and requirements',
    description:
      'Returns detailed requirements, flags, and output configuration for specific selection strategies. Call this tool with candidate strategy IDs before creating or configuring a selection card.',
    execute,
    inputSchema,
  });
}

export default getSelectionStrategyDetailsTool;
